import React, { useState, useEffect, useMemo } from "react";
import { useRef } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { Input, RadioGroup, TextArea } from "@progress/kendo-react-inputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { PageTabItemTitle } from "components/tabs/TabItem/PageTabItemTitle";
import { Button } from "@progress/kendo-react-buttons";
import { TransactionTypes } from "components/Enums/TransactionTypes.ts";
import { Form } from "@progress/kendo-react-form";
import AddressInputComponent from "components/common/AddressInputComponent";
import { useActiveVats } from "services/vatService";
import { useActiveCurrencies } from "services/currencyService";
import { getSupplierCategories } from "services/supplierCategoryService";
import { useNominalsBySubType } from "services/nominalsService";
import {
  addSupplier,
  updateSupplier,
  fetchSupplierById,
} from "services/supplierService";
const data = [
  {
    label: "Disbursement Only",
    value: TransactionTypes.Disbursement,
  },
  {
    label: "Purchase Only",
    value: TransactionTypes.Purchase,
  },
  {
    label: "Both",
    value: TransactionTypes.All,
  },
];

const AddNewSupplier = (props) => {
  const [view, setView] = useState(props.viewType === "Add" ? "Add" : "View");
  const [selected, setSelected] = useState(0);
  const [tabs] = useState(["Details", "Defaults"]);
  const [error, setError] = useState(false);
  const [supplierId, setSupplierId] = useState();
  const [onHold, setOnHold] = useState(false);
  const [payWhenPaid, setPayWhenPaid] = useState(false);
  const [archived, setArchivedValue] = useState(false);
  const [supplierRef, setSupplierRef] = useState();
  const [supplierName, setSupplierName] = useState();
  const [type, setType] = useState();
  const [contactID, setContactID] = useState();
  const [vatCodeId, setVatCodeId] = useState(null);
  const [currencyCode, setCurrencyCode] = useState(null);
  const [selectedNominal, setSelectedNominal] = useState(null);
  const [vatNumber, setVatNumber] = useState();
  const [creditLimit, setCreditLimit] = useState();
  const [termDays, setTermDays] = useState("");
  const [notes, setNotes] = useState();
  const [payeeName, setPayeeName] = useState();
  const [transactionTypes, setTransactionTypes] = useState();
  const [balance, setBalance] = useState();
  const vatCodeIdRef = useRef();
  const [currencyData, setCurrencyData] = useState();
  const [address, setAddress] = useState({});
  const { data: activevatsdata = [], isLoading: vatsLoading } = useActiveVats();
  const { data: activeCurrencies = [] } = useActiveCurrencies();
  const [vatsdata, setvatsData] = useState();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { data: nominalsData = [], loading: nominalsLoading } =
    useNominalsBySubType("ProfitAndLoss_Income");
  const [filteredNominals, setFilteredNominals] = useState([]);

  // Currency data with display field for ComboBox
  // const currencyListWithDisplay = useMemo(() => {
  //   return activeCurrencies.map((item) => ({
  //     ...item,
  //     display: item.currencyCode + "  -  " + item.name + "  -  " + item.symbol,
  //   }));
  // }, [activeCurrencies]);

  useEffect(() => {
    if (activevatsdata && activevatsdata?.length > 0) {
      setvatsData(activevatsdata.slice());
    }
  }, [activevatsdata?.length]);

  useEffect(() => {
    if (nominalsData && nominalsData?.length > 0) {
      setFilteredNominals(nominalsData.slice());
    }
  }, [nominalsData?.length]);

  const changeArchivedValue = (e) => {
    if (view === "View") {
      return;
    }
    setArchivedValue(!archived);
    if (onHold) {
      setOnHold(false);
    }
  };
  const changePayWhenPaid = (e) => {
    if (view === "View") {
      return;
    }
    const newPayWhenPaid = !payWhenPaid;
    setPayWhenPaid(newPayWhenPaid);

    // If "Pay When Paid" is checked, set term days to 0
    if (newPayWhenPaid) {
      setTermDays(0);
    }
  };
  const changeOnHold = (e) => {
    if (view === "View") {
      return;
    }
    setOnHold(!onHold);
    if (archived) {
      setArchivedValue(false);
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getSupplierCategories();
        setCategories(data);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };

    loadCategories();
  }, []);

  const vatCodeReturner = (id) => {
    if (!activevatsdata || !activevatsdata.length) return {};
    return activevatsdata.find((item) => item.vatCodeId === id) || {};
  };

  const currencyCodeReturner = (id) => {
    return activeCurrencies.find((item) => item.currencyCode === id) || {};
  };

  const nominalReturner = (nominalRef) => {
    if (!nominalsData || !nominalsData.length) return null;
    return nominalsData.find((item) => item.nominalRef === nominalRef) || null;
  };

  const handleTermDaysChange = (value) => {
    if (view === "View") return;
    setPayWhenPaid(false);
    setTermDays(value);
  };

  const submitSupplier = async (e) => {
    e.preventDefault();

    if (!supplierRef || !supplierName || !transactionTypes) {
      setError(true);
      return;
    }

    const payload = {
      supplierRef,
      supplierName,
      type: parseInt(type),
      contactID: parseInt(contactID),
      vatCodeId: parseInt(vatCodeIdRef?.vatCodeId),
      currencyCode: currencyCode?.currencyCode,
      nominalRef: selectedNominal?.nominalRef,
      vatNumber,
      creditLimit: parseInt(creditLimit),
      termDays: parseInt(termDays),
      notes,
      payeeName,
      categoryId: selectedCategory || -1,
      transactionTypes,
      balance,
      onHold,
      payWhenPaid,
      archived,
      address,
    };

    try {
      if (view === "Add") {
        await addSupplier(payload);
      } else {
        await updateSupplier(supplierId, payload);
      }

      setError(false);
      props.refresh();
      props.onClose();
      props.refresh();
    } catch (err) {
      console.error("Failed to submit supplier:", err);
    }
  };

  const changeTransactionType = (e) => {
    if (view === "View") {
      return;
    }
    setTransactionTypes(e.value);
  };
  useEffect(() => {
    const loadSupplier = async () => {
      if (props.viewType === "Add" || !props.supplierId) return;

      const data = await fetchSupplierById(props.supplierId);
      if (!data) return;

      setView("View");
      setSupplierId(data.id);
      setOnHold(data.onHold);
      setPayWhenPaid(data.payWhenPaid);
      setArchivedValue(data.archived);
      setSupplierRef(data.supplierRef);
      setSupplierName(data.supplierName);
      setType(data.type);
      setContactID(data.contactID);
      setVatCodeId(vatCodeReturner(data.vatCodeId));
      setCurrencyCode(currencyCodeReturner(data.currencyCode));
      setSelectedNominal(nominalReturner(data.nominalRef));
      setVatNumber(data.vatNumber);
      setCreditLimit(data.creditLimit);
      setTermDays(data.termDays);
      setNotes(data.notes);
      setPayeeName(data.payeeName);
      setSelectedCategory(data.categoryId);
      setTransactionTypes(data.transactionTypes);
      setBalance(data.balance);
      setAddress(data.address);
    };

    loadSupplier();
  }, [props.supplierId, props.viewType]);

  useEffect(() => {
    if (activeCurrencies) {
      setCurrencyData(activeCurrencies.slice());
    }
  }, [activeCurrencies]);

  const filterData = (filter) => {
    const datas = activevatsdata.slice();
    return filterBy(datas, filter);
  };
  const currencyFilterData = (filter) => {
    const currencyData = activeCurrencies.slice();
    return filterBy(currencyData, filter);
  };
  const filterChange = (event) => {
    setvatsData(filterData(event.filter));
  };
  const currencyFilterChange = (event) => {
    setCurrencyData(currencyFilterData(event.filter));
  };

  const nominalsFilterData = (filter) => {
    const nominals = nominalsData.slice();
    return filterBy(nominals, filter);
  };

  const nominalsFilterChange = (event) => {
    setFilteredNominals(nominalsFilterData(event.filter));
  };

  const DetailsTab = () => (
    <div>
      <div className="d-flex " style={{ gap: "25%" }}>
        <div>
          <div className="mt-4">
            <Label className="mb-1">Supplier Ref&nbsp;</Label>
            <div className="">
              <Input
                style={{
                  border: error && !supplierRef ? "1px solid red" : "",
                  minWidth: "300px",
                  width: "300px",
                }}
                value={supplierRef}
                readOnly={view === "View"}
                onChange={(e) => {
                  setSupplierRef(e.target.value);
                }}
                placeholder={"Enter Supplier Ref"}
              />
            </div>
          </div>
          <div className="mt-4">
            <Label className="mb-1">Supplier Name&nbsp;</Label>
            <Input
              style={{
                border: error && !supplierName ? "1px solid red" : "",
              }}
              value={supplierName}
              placeholder={"Enter Supplier Name"}
              readOnly={view === "View"}
              onChange={(e) => {
                setSupplierName(e.target.value);
              }}
            />
          </div>
          <div className="mt-4">
            <div>
              <Label className="mb-1">Payee Name&nbsp;</Label>
            </div>
            <Input
              value={payeeName}
              readOnly={view === "View"}
              placeholder={"Enter Payee Name"}
              onChange={(e) => {
                setPayeeName(e.target.value);
              }}
            />
          </div>
          <div className="mt-4">
            <div>
              <Label className="mb-1">Category&nbsp;</Label>
            </div>
            <ComboBox
              data={categories}
              textField="categoryName"
              dataItemKey="supplierCategoryId"
              disabled={view === "View"}
              value={categories.find(
                (cat) => cat.supplierCategoryId === selectedCategory
              )}
              onChange={(e) => {
                setSelectedCategory(e.value?.supplierCategoryId || -1);
              }}
              placeholder="Select Category"
              iconClassName="wa"
              style={{ width: "300px" }}
            />
          </div>
        </div>
        <div>
          <div className="mt-4">
            <div className="label">
              <Label>Transaction Types:&nbsp;</Label>
            </div>
            <div
              className={`mt-3 ${error && !transactionTypes ? "error" : ""}`}
            >
              <RadioGroup
                data={data}
                readOnly={view === "View"}
                className="gap-2 radio"
                value={transactionTypes}
                onChange={changeTransactionType}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Label className="label mb-2">Notes&nbsp;</Label>
        <div>
          <TextArea
            value={notes}
            style={{ maxWidth: "none", width: "90%", height: "60px" }}
            placeholder={"Enter Details"}
            readOnly={view === "View"}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
          />
        </div>
      </div>
      <div className="mt-4">
        <div className="d-flex radio">
          <Checkbox
            defaultChecked={false}
            onClick={changeOnHold}
            value={onHold}
            label={"On Hold"}
          />
        </div>
      </div>
      <div className="mt-4">
        <div className="d-flex radio">
          <Checkbox
            defaultChecked={false}
            value={archived}
            onClick={changeArchivedValue}
            label={"Archived"}
          />
        </div>
      </div>
    </div>
  );
  const DefaultsTab = () => (
    <div>
      <div className="d-flex ">
        <div className="col-5 me-5 ">
          <div className="mt-3">
            <Label className="mb-1 d-block">Vat Number&nbsp;</Label>
            <div>
              <Input
                style={{
                  width: "300px",
                }}
                value={vatNumber}
                readOnly={view === "View"}
                placeholder={"Enter VAT Number"}
                onChange={(e) => {
                  setVatNumber(e.target.value);
                }}
              />
            </div>
          </div>
          <div className="mt-3">
            <Label className="mb-1 d-block">Vat Code&nbsp;</Label>
            <ComboBox
              data={vatsdata}
              style={{
                width: "300px",
              }}
              textField="display"
              filterable={true}
              onFilterChange={filterChange}
              placeholder={"Search VAT Code"}
              value={vatCodeId}
              iconClassName="wa"
              loading={vatsLoading}
              disabled={view === "View"}
              onChange={(e) => {
                setVatCodeId(e.value);
              }}
            />
          </div>
          <div className="mt-3">
            <Label className="mb-1 d-block">Currency Code&nbsp;</Label>
            <ComboBox
              data={currencyData}
              style={{
                width: "300px",
              }}
              textField={"display"}
              iconClassName="wa"
              filterable={true}
              onFilterChange={currencyFilterChange}
              placeholder={"Search Currency Code"}
              disabled={view === "View"}
              onChange={(e) => {
                if (view === "View") {
                  return;
                }
                setCurrencyCode(e.value);
              }}
              value={currencyCode}
            />
          </div>
        </div>
        <div className="ms-2">
          <div className="mt-3">
            <Label className="mb-1">Credit Limit&nbsp;</Label>
            <div>
              <Input
                value={creditLimit}
                readOnly={view === "View"}
                style={{ minWidth: "300px" }}
                placeholder={"Enter Limit"}
                type="number"
                onChange={(e) => {
                  setCreditLimit(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="mt-3">
            <Label className="mb-1 d-block">Default Nominal&nbsp;</Label>
            <ComboBox
              data={filteredNominals}
              style={{
                width: "300px",
              }}
              textField="display"
              dataItemKey="nominalRef"
              disabled={view === "View"}
              filterable={true}
              onFilterChange={nominalsFilterChange}
              placeholder={"Search Default Nominal"}
              value={selectedNominal}
              iconClassName="wa"
              loading={nominalsLoading}
              readOnly={view === "View"}
              onChange={(e) => {
                if (view === "View") return;
                setSelectedNominal(e.value);
              }}
            />
          </div>
          <Label className="mb-1 mt-3 d-block">Payment Terms&nbsp;</Label>
          <div className="mt-3 d-flex align-items-center">
            <Label className="mb-1 ms-3 me-2 w-50">Due in Days&nbsp;</Label>
            <Input
              value={termDays}
              readOnly={view === "View"}
              type="number"
              min="0"
              placeholder={"Enter Due in Days"}
              onChange={(e) => {
                handleTermDaysChange(e.target.value);
              }}
            />
          </div>
          <div className="mt-2">
            <div className="d-flex radio fs-7  ms-3">
              <Checkbox
                defaultChecked={false}
                value={payWhenPaid}
                onClick={changePayWhenPaid}
                label={"Pay When Paid"}
              />
            </div>
          </div>
        </div>
      </div>
      <Form
        initialValues={address}
        render={() => (
          <AddressInputComponent
            name="Address"
            readOnly={view === "View"}
            address={address}
            setAddress={setAddress}
            width={"300px"}
            contactComponent={`col-5 me-5`}
          />
        )}
      ></Form>
    </div>
  );

  const newSupplier = (
    <div className="">
      <Dialog
        title={`${view} ${
          props.type === "Consultant" ? "Consultant" : "Supplier"
        }`}
        onClose={props.onClose}
        className="add-dialog dialog-md"
      >
        <TabStrip
          keepTabsMounted={true}
          selected={selected}
          onSelect={(e) => setSelected(e.selected)}
          className="mt-4 ps-5 pagetab companytab w-100"
        >
          {tabs.map((item, index) => {
            return (
              <TabStripTab
                title={
                  <PageTabItemTitle
                    className={`mb-sm-4 mb-xl-0`}
                    title={item}
                    index={tabs.indexOf(item)}
                    selected={selected}
                  />
                }
                key={index}
              >
                <div className="mt-3 w-100">
                  {item === "Details" && DetailsTab()}
                  {item === "Defaults" && DefaultsTab()}
                </div>
              </TabStripTab>
            );
          })}
        </TabStrip>
        <DialogActionsBar>
          <div className="dialog-footer mt-1">
            <Button
              className={`k-button common-btn bgBtnSecondary me-3`}
              onClick={props.onClose}
            >
              {view === "View" ? "Back" : "Cancel"}
            </Button>
            {view !== "View" && (
              <Button
                type="submit"
                className="k-button common-btn me-3"
                onClick={submitSupplier}
              >
                Save
              </Button>
            )}
            {view === "View" && (
              <Button
                className="common-btn me-3"
                onClick={() => setView("Edit")}
              >
                Edit
              </Button>
            )}
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
  return <div className="gridMargin">{newSupplier}</div>;
};
export default AddNewSupplier;
